/**
 * VISIBILITY CUSTOM ELEMENT
 * Abstracted from several other components that handle toggling state. Created for DRYness.
 *
 * Closed overlay panels are parked with `opacity-0` / `translate-x-full` rather than removed,
 * which keeps a full-viewport compositor layer alive for every modal, drawer and lightbox on
 * the page. This base class takes those panels out of the paint tree with `display: none`
 * while closed, and restores them a frame before the open transition runs.
 */

const PANEL_TRANSITION_FALLBACK = 700
const OVERLAY_POSITIONS = ['fixed', 'absolute']

export class VisibilityElement extends HTMLElement {
  handleShow: () => void
  handleHide: () => void
  handleToggle: () => void
  handleDisable: () => void
  handleEventDisable: (event: any) => void
  handleEventShow: (event: any) => void
  handleEventHide: (event: any) => void

  private panels: HTMLElement[] | null = null
  private panelHideTimer: number | null = null
  private panelSetupFrame: number | null = null
  private baseListenersAttached = false

  constructor() {
    super()

    // CUSTOM METHODS
    // Adding custom methods here to deal with 'this' issues

    this.handleShow = () => {
      this.revealPanels()
      this.dataset.ui = 'visible'
    }

    this.handleHide = () => {
      this.dataset.ui = undefined
      this.schedulePanelHide()
    }

    this.handleDisable = () => {
      this.dataset.ui = 'disabled'
      this.schedulePanelHide()
    }

    this.handleToggle = () => {
      if (this.dataset.ui === 'visible') this.handleHide()
      else this.handleShow()
    }

    this.handleEventShow = (event) => {
      if (event.detail.target !== this.dataset.id) return
      this.handleShow()
    }

    this.handleEventHide = (event) => {
      if (event.detail.target !== this.dataset.id) return
      this.handleHide()
    }

    this.handleEventDisable = (event) => {
      if (event.detail.target !== this.dataset.id) return
      this.handleDisable()
    }
  }

  // Event Dispatchers
  static EVENT_SHOW = 'show'
  static dispatchShow(id: string) {
    document.dispatchEvent(
      new CustomEvent(VisibilityElement.EVENT_SHOW, {
        detail: {
          target: id
        }
      })
    )
  }

  static EVENT_HIDE = 'hide'
  static dispatchHide(id: string) {
    document.dispatchEvent(
      new CustomEvent(VisibilityElement.EVENT_HIDE, {
        detail: {
          target: id
        }
      })
    )
  }

  static EVENT_DISABLE = 'disable'
  static dispatchDisable(id: string) {
    document.dispatchEvent(
      new CustomEvent(VisibilityElement.EVENT_DISABLE, {
        detail: {
          target: id
        }
      })
    )
  }

  /**
   * Direct children that act as overlay surfaces (the panel itself and its backdrop).
   * Scanning only direct children keeps this cheap and avoids claiming panels that
   * belong to a nested VisibilityElement.
   *
   * A child only qualifies if it is both taken out of flow *and* non-interactive in the
   * closed state. Some components (the PDP gallery, for one) use a VisibilityElement as
   * a plain layout container, and their children must never be hidden — position alone
   * is not enough to tell the two apart.
   */
  private collectPanels(): HTMLElement[] {
    return Array.from(this.children).filter((child): child is HTMLElement => {
      if (!(child instanceof HTMLElement)) return false
      if (child instanceof VisibilityElement) return false

      const style = window.getComputedStyle(child)
      if (!OVERLAY_POSITIONS.includes(style.position)) return false

      return style.pointerEvents === 'none' || style.opacity === '0' || style.visibility === 'hidden'
    })
  }

  private getPanels(): HTMLElement[] {
    if (!this.panels) {
      this.panels = this.collectPanels()
    }
    return this.panels
  }

  private get isOpen(): boolean {
    return this.dataset.ui === 'visible'
  }

  private clearPanelHideTimer() {
    if (this.panelHideTimer !== null) {
      window.clearTimeout(this.panelHideTimer)
      this.panelHideTimer = null
    }
  }

  private revealPanels() {
    this.clearPanelHideTimer()
    const panels = this.getPanels()
    if (!panels.length) return

    panels.forEach((panel) => {
      panel.style.removeProperty('display')
    })

    // Force a reflow so the browser has a "closed" frame to transition away from,
    // otherwise the panel pops in without animating.
    this.getBoundingClientRect()
  }

  /**
   * Waits for the close transition to finish before taking the layer down, so the
   * panel doesn't disappear mid-animation.
   */
  private schedulePanelHide() {
    this.clearPanelHideTimer()
    const panels = this.getPanels()
    if (!panels.length) return

    this.panelHideTimer = window.setTimeout(() => {
      this.panelHideTimer = null
      if (this.isOpen) return
      this.hidePanels()
    }, this.longestPanelTransition())
  }

  private longestPanelTransition(): number {
    const durations = this.getPanels().map((panel) => {
      const style = window.getComputedStyle(panel)
      const toMs = (value: string) =>
        value
          .split(',')
          .map((part) => {
            const trimmed = part.trim()
            const numeric = parseFloat(trimmed)
            if (isNaN(numeric)) return 0
            return trimmed.endsWith('ms') ? numeric : numeric * 1000
          })
          .reduce((max, current) => Math.max(max, current), 0)

      return toMs(style.transitionDuration) + toMs(style.transitionDelay)
    })

    const longest = durations.reduce((max, current) => Math.max(max, current), 0)
    return Math.min(longest + 50, PANEL_TRANSITION_FALLBACK)
  }

  private hidePanels() {
    this.getPanels().forEach((panel) => {
      panel.style.display = 'none'
    })
  }

  addEventListeners() {
    if (this.baseListenersAttached) return
    this.baseListenersAttached = true
    document.addEventListener(VisibilityElement.EVENT_SHOW, this.handleEventShow)
    document.addEventListener(VisibilityElement.EVENT_HIDE, this.handleEventHide)
    document.addEventListener(VisibilityElement.EVENT_DISABLE, this.handleEventDisable)
  }

  removeEventListeners() {
    if (!this.baseListenersAttached) return
    this.baseListenersAttached = false
    document.removeEventListener(VisibilityElement.EVENT_SHOW, this.handleEventShow)
    document.removeEventListener(VisibilityElement.EVENT_HIDE, this.handleEventHide)
    document.removeEventListener(VisibilityElement.EVENT_DISABLE, this.handleEventDisable)
  }

  connectedCallback() {
    this.addEventListeners()

    // Deferred so the initial scan doesn't add a style recalc to first paint.
    this.panelSetupFrame = requestAnimationFrame(() => {
      this.panelSetupFrame = null
      this.panels = null
      if (!this.isOpen) this.hidePanels()
    })
  }

  disconnectedCallback() {
    this.removeEventListeners()
    this.clearPanelHideTimer()

    if (this.panelSetupFrame !== null) {
      cancelAnimationFrame(this.panelSetupFrame)
      this.panelSetupFrame = null
    }

    this.panels = null
  }
}
